import React, {
  useState,
  useRef,
  ReactNode,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { JemGenContext } from "./jemGenContext";
import {
  Ard,
  CodeId,
  DescribeChartOfAccountsCommand,
  FuseAccountingCentralDatastoreClient,
  GetArdCommand,
  GetArdResponse,
  ListRegistrationsCommand,
  Registration,
  UseCaseInclusion,
  UseCase,
} from "@amzn/fuse-accounting-central-datastore-client";
import { InitDomain } from "../domain/domainContext";
import { ArdRegionMap, ModalMode, RegistrationsLoadingState } from "./types";
import { Domain } from "../../config/BartLambdaConfig";
import { Auth } from "aws-amplify";
import { getFacdConfig } from "src/config/fuse-accounting-central-datastore-config";
import { isProd } from "src/util";

// Derive segment field names from CodeId.Visitor type (excludes the _ catch-all)
type SegmentField = Exclude<keyof CodeId.Visitor<void>, "_">;
const SEGMENT_FIELDS: SegmentField[] = [
  "companyCode",
  "location",
  "costCenter",
  "accountNumber",
  "productLine",
  "channel",
  "project",
];

/**
 * Extracts unique segment CodeIds from an array of use cases.
 * @param usecases Array of UseCase objects to extract segments from
 * @param existingKeys Optional set of keys to exclude (for incremental updates)
 * @returns Map of segment key to CodeId object
 */
const extractSegmentCodeIds = (
  usecases: UseCase[] | undefined,
  existingKeys?: Set<string>,
): Map<string, CodeId> => {
  const codeIdsMap = new Map<string, CodeId>();

  usecases?.forEach((useCase) => {
    useCase.uslLines?.forEach((uslLine) => {
      SEGMENT_FIELDS.forEach((field) => {
        const value = uslLine[field];
        if (value) {
          const key = `${field}:${value}`;
          if (
            !codeIdsMap.has(key) &&
            (!existingKeys || !existingKeys.has(key))
          ) {
            codeIdsMap.set(key, { [field]: value } as unknown as CodeId);
          }
        }
      });
    });
  });

  return codeIdsMap;
};

/**
 * Builds a descriptions map from the DescribeChartOfAccountsCommand response.
 * @param descriptions The descriptions object from the API response
 * @returns Map of segment key to codeName
 */
const buildDescriptionsMap = (
  descriptions: Record<string, { codeName?: string }> | undefined,
): Map<string, string> => {
  const descriptionsMap = new Map<string, string>();
  if (descriptions) {
    Object.entries(descriptions).forEach(([key, description]) => {
      if (description?.codeName) {
        descriptionsMap.set(key, description.codeName);
      }
    });
  }
  return descriptionsMap;
};

interface JemGenContextProviderProps {
  children: ReactNode;
}

export const JemGenContextProvider: React.FC<JemGenContextProviderProps> = ({
  children,
}) => {
  const [selectedArd, setSelectedArd] = useState<Ard | null>(null);
  const [selectedArdDetailed, setSelectedArdDetailed] =
    useState<GetArdResponse | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(ModalMode.CREATE);
  const [existingArds, setExistingArds] = useState<Ard[]>([]);
  const [toolsContentSelection, setToolsContentSelection] = useState<
    string | null
  >(null);
  const [splitPanelSize, setSplitPanelSize] = useState<number>(
    typeof window !== "undefined" ? window.innerHeight * 0.4 : 0,
  );
  const [registrationsLoadingState, setRegistrationsLoadingState] =
    useState<RegistrationsLoadingState>("not-started");
  const [availableRegistrations, setAvailableRegistrations] = useState<
    Registration[]
  >([]);
  const [coaDescriptions, setCoaDescriptions] = useState<Map<string, string>>(
    new Map(),
  );
  const [isLoadingCoaDescriptions, setIsLoadingCoaDescriptions] =
    useState(false);
  const ardRegionMapRef = useRef<ArdRegionMap>(new Map());
  const facdClientMapRef = useRef(
    new Map<string, FuseAccountingCentralDatastoreClient>(),
  );
  // Track which COA keys have been queried to avoid infinite loops and duplicate requests
  const queriedCoaKeysRef = useRef<Set<string>>(new Set());

  const domainData = useContext(InitDomain);

  const getFacdClient = useCallback(
    async (
      domain: Domain,
    ): Promise<FuseAccountingCentralDatastoreClient | null> => {
      const existingClient = facdClientMapRef.current.get(domain);
      if (existingClient) {
        return existingClient;
      }

      // Create new client if it doesn't exist
      try {
        const credentials = await Auth.currentCredentials();
        const essentialCredentials = Auth.essentialCredentials(credentials);
        const newClient = new FuseAccountingCentralDatastoreClient({
          ...getFacdConfig(domain),
          credentials: essentialCredentials,
        });

        facdClientMapRef.current.set(domain, newClient);

        // Set timeout to delete the client after 30 minutes (1800000 ms)
        setTimeout(
          () => {
            facdClientMapRef.current.delete(domain);
          },
          30 * 60 * 1000,
        );

        return newClient;
      } catch (error) {
        console.error(
          `Error creating FACD client for domain ${domain}:`,
          error,
        );
        return null;
      }
    },
    [],
  );

  const setArdRegionMap = (map: ArdRegionMap) => {
    ardRegionMapRef.current = map;
  };

  // Fetch ARD data function moved from ard-edit-modal
  const fetchArdData = async (ardId: string) => {
    setSelectedArdDetailed(null);
    try {
      const ardRegion = ardRegionMapRef.current.get(ardId);
      if (ardRegion === undefined) {
        throw Error(`Unknown region for ARD ${ardId}`);
      }
      const facdClient = await getFacdClient(ardRegion);

      if (facdClient) {
        const getArdCommand = new GetArdCommand({
          ardId: ardId,
          useCaseInclusion: UseCaseInclusion.ALL,
          withHistory: true,
          withAssociatedPlans: true,
        });

        const response = await facdClient.send(getArdCommand);

        if (response && response.ard) {
          setSelectedArdDetailed(response);
        }
      } else {
        console.error("No FACD client found for region:", ardRegion);
      }
    } catch (error) {
      console.error("Error fetching ARD data for selectedArdDetailed:", error);
    }
  };

  // Automatically fetch detailed ARD data when selectedArd changes
  useEffect(() => {
    if (selectedArd?.id) {
      console.log(
        "SelectedArd changed, fetching detailed data:",
        selectedArd.id,
      );
      setSelectedArdDetailed(null);
      fetchArdData(selectedArd.id);
    } else {
      console.log("No selectedArd ID, clearing selectedArdDetailed");
      setSelectedArdDetailed(null);
    }
  }, [selectedArd?.id, domainData.domain, existingArds]);

  // Fetch available registrations when selectedArdDetailed changes.
  // Uses AbortController to cancel stale requests on rapid ARD selection.
  useEffect(() => {
    const abortController = new AbortController();

    const fetchRegistrations = async () => {
      const ardId = selectedArdDetailed?.ard?.id;
      if (!ardId) {
        setAvailableRegistrations([]);
        setRegistrationsLoadingState("loaded");
        return;
      }

      const domain = ardRegionMapRef.current.get(ardId);
      if (domain === undefined) {
        console.error(`Unknown region for ARD ${ardId}`);
        setAvailableRegistrations([]);
        setRegistrationsLoadingState("loaded");
        return;
      }

      setRegistrationsLoadingState("loading");

      try {
        const facdClient = await getFacdClient(domain);
        if (!facdClient) {
          console.error("No FACD client found for region:", domain);
          setAvailableRegistrations([]);
          return;
        }

        const listRegistrationsCommand = new ListRegistrationsCommand({
          ardId,
        });

        const response = await facdClient.send(listRegistrationsCommand, {
          abortSignal: abortController.signal,
        });

        if (response && response.registrations) {
          setAvailableRegistrations(response.registrations);
        } else {
          setAvailableRegistrations([]);
        }
      } catch (error) {
        // Ignore aborted requests — a newer request has taken over
        if (abortController.signal.aborted) return;
        console.error("An error occurred in listRegistrations:", error);
        setAvailableRegistrations([]);
      } finally {
        if (!abortController.signal.aborted) {
          setRegistrationsLoadingState("loaded");
        }
      }
    };

    fetchRegistrations();
    return () => abortController.abort();
  }, [selectedArdDetailed?.ard?.id, getFacdClient]);

  // Derive the first available registration ID from plans or ListRegistrations (single source of truth)
  const availableRegistrationId = React.useMemo((): string | null => {
    // Check if any associated plan already has a registration
    const plans = selectedArdDetailed?.ard?.associatedPlans || [];
    for (const plan of plans) {
      if (plan.accountingRegistrationId) {
        return plan.accountingRegistrationId;
      }
    }
    // Fall back to registrations returned from ListRegistrationsCommand
    if (availableRegistrations.length > 0) {
      return availableRegistrations[0].accountingRegistrationId || null;
    }
    return null;
  }, [selectedArdDetailed?.ard?.associatedPlans, availableRegistrations]);

  /**
   * Fetches COA descriptions for the given CodeIds and updates the state.
   * Always merges new descriptions with existing state to avoid race conditions.
   * @param codeIdsMap Map of segment keys to CodeId objects to fetch
   */
  const fetchAndSetCoaDescriptions = useCallback(
    async (codeIdsMap: Map<string, CodeId>) => {
      if (codeIdsMap.size === 0) {
        return;
      }

      setIsLoadingCoaDescriptions(true);

      try {
        const region = isProd() ? "prod-FE" : "devo-FE";
        const facdClient = await getFacdClient(region);

        if (!facdClient) {
          console.error(`Failed to get FACD client for region '${region}'`);
          return;
        }

        const codeIds = Array.from(codeIdsMap.values());
        const command = new DescribeChartOfAccountsCommand({ codeIds });
        const response = await facdClient.send(command);

        const newDescriptions = buildDescriptionsMap(response.descriptions);

        // Use functional update to merge with latest state (avoids race conditions)
        setCoaDescriptions((prev) => {
          const merged = new Map(prev);
          newDescriptions.forEach((value, key) => {
            merged.set(key, value);
          });
          return merged;
        });
      } catch (error) {
        console.error("Failed to fetch COA descriptions:", error);
      } finally {
        setIsLoadingCoaDescriptions(false);
      }
    },
    [],
  );

  // Fetch COA descriptions for all 7 segments when existingArds changes
  useEffect(() => {
    const codeIdsMap = new Map<string, CodeId>();
    existingArds.forEach((ard) => {
      const ardCodeIds = extractSegmentCodeIds(ard.usecases);
      ardCodeIds.forEach((value, key) => {
        if (!codeIdsMap.has(key)) {
          codeIdsMap.set(key, value);
        }
      });
    });

    // Reset tracked keys since we're replacing all descriptions
    queriedCoaKeysRef.current = new Set(codeIdsMap.keys());
    fetchAndSetCoaDescriptions(codeIdsMap);
  }, [existingArds, fetchAndSetCoaDescriptions]);

  // Fetch COA descriptions for new segments when selectedArdDetailed changes
  useEffect(() => {
    if (!selectedArdDetailed?.ard?.usecases) {
      return;
    }

    // Extract segment values not already queried (using ref to avoid infinite loop)
    const newCodeIds = extractSegmentCodeIds(
      selectedArdDetailed.ard.usecases,
      queriedCoaKeysRef.current,
    );

    if (newCodeIds.size > 0) {
      // Track queried keys in ref BEFORE fetch to prevent duplicate requests
      newCodeIds.forEach((_, key) => queriedCoaKeysRef.current.add(key));
      fetchAndSetCoaDescriptions(newCodeIds);
    }
  }, [selectedArdDetailed, fetchAndSetCoaDescriptions]);

  return (
    <JemGenContext.Provider
      value={{
        existingArds,
        setExistingArds,
        selectedArd,
        setSelectedArd,
        selectedArdDetailed,
        setSelectedArdDetailed,
        getFacdClient,
        ardRegionMap: ardRegionMapRef.current,
        setArdRegionMap,
        modalMode,
        setModalMode,
        toolsContentSelection,
        setToolsContentSelection,
        coaDescriptions,
        isLoadingCoaDescriptions,
        fetchArdData,
        splitPanelSize,
        setSplitPanelSize,
        registrationsLoadingState,
        setRegistrationsLoadingState,
        availableRegistrations,
        availableRegistrationId,
      }}
    >
      {children}
    </JemGenContext.Provider>
  );
};
